// Keep plan.about.circleOfSupport mirrored from plan.about.whoLivesWithMe
// so every "Who Lives With Me?" entry has a paired Circle-of-Support row.
//
// Called from every controller that writes the plan to localStorage
// (wizard, plan-store, dynamic-list) so the link holds regardless of
// which surface authored the change.
//
// Linkage:
//   - CoS row's `linkedFromWhoLives` carries the whoLivesWithMe item's id.
//   - Removing a whoLives item removes the linked CoS row.
//   - Editing the whoLives text updates the linked CoS row's `name`.
//   - The CoS row's `relationship` is a default the user may freely
//     change — we don't clobber it on subsequent syncs.

// Normalise a Circle/household name for matching: strip "[Age]" and
// "(Role)" decorations and a trailing "— note", collapse whitespace,
// lowercase. Two rows for the same person compare equal even when one is
// age-prefixed ("Ava [8]") and the other role-suffixed ("Ava (Sister)").
export function normCosName(s) {
  return String(s || "")
    .replace(/\[[^\]]+\]/g, "")
    .replace(/\([^()]+\)/g, "")
    .replace(/—.*$/, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
}

// Collapse duplicate Circle-of-Support rows. Two independent mirrors
// (this one, run on save; dynamic_list #_syncFromHousehold, run when the
// CoS list seeds/opens) can each add a row for the same household person —
// e.g. opening a .sdp whose rows aren't linked yet. This folds them:
//   1. rows sharing a linkedFromWhoLives id → keep the first
//   2. an UNLINKED row whose FULL normalised name equals a LINKED row →
//      drop it (it's a manual/legacy duplicate of a household member).
// Uses full-name equality so distinct people who share a first name
// ("John Smith" vs "John Doe") are never merged.
export function dedupeCircle(cos) {
  if (!Array.isArray(cos)) return
  const seen = new Set()
  for (let i = 0; i < cos.length; i++) {
    const c = cos[i]
    if (c && c.linkedFromWhoLives != null) {
      if (seen.has(c.linkedFromWhoLives)) { cos.splice(i, 1); i--; continue }
      seen.add(c.linkedFromWhoLives)
    }
  }
  const linkedNames = new Set()
  cos.forEach(c => { if (c && c.linkedFromWhoLives != null) { const n = normCosName(c.name); if (n) linkedNames.add(n) } })
  for (let i = cos.length - 1; i >= 0; i--) {
    const c = cos[i]
    if (c && c.linkedFromWhoLives == null) {
      const n = normCosName(c.name)
      if (n && linkedNames.has(n)) cos.splice(i, 1)
    }
  }
}

export function syncWhoLivesIntoCircle(plan) {
  if (!plan || typeof plan !== "object") return
  if (!plan.about || typeof plan.about !== "object") return
  if (!Array.isArray(plan.about.whoLivesWithMe)) return
  if (!Array.isArray(plan.about.circleOfSupport)) plan.about.circleOfSupport = []

  const wlw = plan.about.whoLivesWithMe
  const cos = plan.about.circleOfSupport

  // 1. Drop linked CoS rows whose whoLives source is gone.
  const wlwIds = new Set(wlw.map(it => it && it.id))
  for (let i = cos.length - 1; i >= 0; i--) {
    const c = cos[i]
    if (c && c.linkedFromWhoLives != null && !wlwIds.has(c.linkedFromWhoLives)) {
      cos.splice(i, 1)
    }
  }

  // 2. Adopt-or-add. For each whoLives item:
  //    a. If a CoS row is already linked → just keep its name in sync.
  //    b. Otherwise check for an EXISTING unlinked CoS row whose
  //       normalised name matches the whoLives entry. If one exists,
  //       adopt it (set linkedFromWhoLives) so we don't create a
  //       second row for the same person.
  //    c. Only push a brand-new row when neither (a) nor (b) found
  //       a match.
  const norm = normCosName
  wlw.forEach(it => {
    if (!it || it.id == null) return
    const text = (it.text == null ? "" : String(it.text)).trim()
    const linked = cos.find(c => c && c.linkedFromWhoLives === it.id)
    if (linked) {
      if (linked.name !== text) linked.name = text
      return
    }
    const wantKey = norm(text)
    const dupe = wantKey
      ? cos.find(c => c && c.linkedFromWhoLives == null && norm(c.name) === wantKey)
      : null
    if (dupe) {
      dupe.linkedFromWhoLives = it.id
      dupe.name = text
      return
    }
    cos.push({
      id: Date.now() + Math.random(),
      name: text,
      relationship: _defaultRelationship(text),
      linkedFromWhoLives: it.id
    })
  })

  // Heal any duplicates already in the plan (same-link rows, or a manual
  // row duplicating a linked household member) so opening an older/legacy
  // .sdp collapses to one row per person.
  dedupeCircle(cos)
}

function _defaultRelationship(text) {
  // Extract the role from the "(Role)" scaffold so a brand-new row
  // ("Maria (Mom)") starts as Family instead of falling back to TBD.
  const m = (text || "").match(/\(([^)]+)\)/)
  const raw = m ? m[1].trim() : ""
  // Strip trailing ", age 12" qualifier the wizard adds for minor siblings.
  const role = raw.replace(/,\s*age\s+\d+\s*$/i, "").trim().toLowerCase()
  if (role === "caregiver") return "Professional"
  if (role === "roommate")  return "Community"
  return "Family"
}
